import type { DocumentMeta, Point, Rect } from '../core/types';
import { getEngine } from './engine';

export type HistogramChannel = 'rgb' | 'r' | 'g' | 'b' | 'luma';

const IDENTITY_VIEW = { zoom: 1, centerX: 0, centerY: 0, rotation: 0 };

/** 256 bins, counts as floats 0..1 normalized to max bin. */
export function computeHistogram(
  pixels: Uint8Array,
  width: number,
  height: number,
  channel: HistogramChannel,
): Float32Array {
  const bins = new Float32Array(256);
  const count = width * height;

  for (let i = 0; i < count; i += 1) {
    const offset = i * 4;
    const r = pixels[offset];
    const g = pixels[offset + 1];
    const b = pixels[offset + 2];

    let value: number;
    switch (channel) {
      case 'r':
        value = r;
        break;
      case 'g':
        value = g;
        break;
      case 'b':
        value = b;
        break;
      case 'luma':
        value = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        break;
      default:
        value = Math.max(r, g, b);
        break;
    }
    bins[value] += 1;
  }

  let peak = 0;
  for (let i = 0; i < 256; i += 1) peak = Math.max(peak, bins[i]);
  if (peak > 0) {
    for (let i = 0; i < 256; i += 1) bins[i] /= peak;
  }
  return bins;
}

/** Reads the active layer surface at full document resolution. */
export function readHistogramPixels(
  doc: DocumentMeta,
): { pixels: Uint8Array; width: number; height: number } | null {
  const engine = getEngine();
  if (!engine.available) return null;

  const layerId = doc.activeLayerId;
  if (!layerId) return null;

  const layer = engine.resourcesFor(doc).layer(layerId);
  if (!layer) return null;

  const { width, height } = layer.surface;
  if (width <= 0 || height <= 0) return null;

  return { pixels: layer.surface.readPixels(), width, height };
}

export function sampleDocumentHistogram(
  doc: DocumentMeta,
  channel: HistogramChannel,
): Float32Array | null {
  const source = readHistogramPixels(doc);
  if (!source) return null;
  return computeHistogram(source.pixels, source.width, source.height, channel);
}

export function sampleDocumentPixel(
  doc: DocumentMeta,
  point: Point,
  fromComposite: boolean,
): { r: number; g: number; b: number } | null {
  const engine = getEngine();
  if (!engine.available) return null;

  const x = Math.floor(point.x);
  const y = Math.floor(point.y);
  if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return null;

  const rect: Rect = { x, y, width: 1, height: 1 };
  const resources = engine.resourcesFor(doc);
  const pixels = fromComposite
    ? engine
        .compositeToTexture(doc, { view: IDENTITY_VIEW, showSelection: false })
        .readPixels(rect)
    : resources.layer(doc.activeLayerId ?? '')?.surface.readPixels(rect);
  if (!pixels || pixels.length < 3) return null;

  return { r: pixels[0], g: pixels[1], b: pixels[2] };
}
